import { useEffect, useMemo, useState } from "react";
import { ToggleTabs } from "../ui/ToggleTabs";
import { ExportDropdown, type ExportColumn } from "../ui/ExportDropdown";
import { buLabel } from "../../format";
import { fmtBRL } from "../../utils/formatters";
import { formatarDataCurtaBr, ymdIso } from "../../utils/dateBr";
import { brlInt, dateCell, num } from "../../utils/xlsxHelpers";
import { getLiquidacoes } from "../../api/client";
import type { LiquidacaoRow } from "../../api/types";
import type { BuFluxo } from "../../utils/mockData";

type Tab = "liq6du" | "net";
type FiltroBu = BuFluxo | "todas";

interface Props {
  filtroBu: FiltroBu;
  /** Data oficial do próximo 6du CCEE — vem resolvida do pai via
   * `useProximo6du`. `null` enquanto o hook não terminou: o componente
   * segura o fetch e mantém o loading state. Fix #158: antes calculava
   * local via `calcularProximo6DU(new Date())`, heurística que ignora
   * feriados móveis (Corpus Christi) e errava a data por 1 dia útil. */
  dataAlvo: Date | null;
  /** Task #45: líquido do card 6DU (/api/fluxo, dust-INCLUSIVE) pra reconciliar
   * EXATO no rodapé. A tabela filtra trades-poeira (< R$0,50, _ZERO_TOLERANCE)
   * mas o card não → a diferença (card − Total vigente exibido) é o dust. Só
   * faz sentido com filtroBu="todas" (o card é all-BU). null = sem reconciliação
   * (loading/mock/BU filtrada). */
  cardLiquido6du?: number | null;
}

function _toNum(s: string | null): number {
  if (s === null) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function _ymdToDate(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

const _FMT_VOL = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});
const _FMT_PRECO = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function _fmtVol(v: number): string {
  return _FMT_VOL.format(Math.abs(v));
}

function _fmtPreco(v: number): string {
  return `R$ ${_FMT_PRECO.format(v)}`;
}

function _tipoLabel(tipo: "compra" | "venda"): string {
  return tipo === "compra" ? "COMPRA" : "VENDA";
}

interface State {
  rows: LiquidacaoRow[];
  loading: boolean;
}

interface NetRow {
  contraparte: string;
  bu: BuFluxo;
  compras_R: number;
  vendas_R: number;
  net_fin: number; // vendas - compras
  vol_compras: number;
  vol_vendas: number;
  net_eng: number; // vol_compras - vol_vendas (positivo long, negativo short)
}

function _aggregateNet(rows: LiquidacaoRow[]): NetRow[] {
  const acc = new Map<string, NetRow>();
  for (const r of rows) {
    if (r.superseded_by != null) continue;
    if (r.business_unit !== "atacado" && r.business_unit !== "varejo") continue;
    const bu = r.business_unit as BuFluxo;
    const key = `${r.contraparte_nome}|${bu}`;
    let bucket = acc.get(key);
    if (!bucket) {
      bucket = {
        contraparte: r.contraparte_nome,
        bu,
        compras_R: 0,
        vendas_R: 0,
        net_fin: 0,
        vol_compras: 0,
        vol_vendas: 0,
        net_eng: 0,
      };
      acc.set(key, bucket);
    }
    const valor = Math.abs(_toNum(r.valor));
    const vol = Math.abs(_toNum(r.volume));
    if (r.tipo === "compra") {
      bucket.compras_R += valor;
      bucket.vol_compras += vol;
    } else {
      bucket.vendas_R += valor;
      bucket.vol_vendas += vol;
    }
  }
  for (const b of acc.values()) {
    b.net_fin = b.vendas_R - b.compras_R;
    b.net_eng = b.vol_compras - b.vol_vendas;
  }
  // Sort default: ABS(net_fin) DESC.
  return Array.from(acc.values()).sort((a, b) => Math.abs(b.net_fin) - Math.abs(a.net_fin));
}

interface NetTotal {
  compras_R: number;
  vendas_R: number;
  net_fin: number;
  vol_compras: number;
  vol_vendas: number;
  net_eng: number;
}

function _totalNet(rows: NetRow[]): NetTotal {
  const t: NetTotal = { compras_R: 0, vendas_R: 0, net_fin: 0, vol_compras: 0, vol_vendas: 0, net_eng: 0 };
  for (const r of rows) {
    t.compras_R += r.compras_R;
    t.vendas_R += r.vendas_R;
    t.net_fin += r.net_fin;
    t.vol_compras += r.vol_compras;
    t.vol_vendas += r.vol_vendas;
    t.net_eng += r.net_eng;
  }
  return t;
}

/** Subtotais do rodapé da aba detalhada (Task #42).
 *
 * A tabela é de CONFERÊNCIA trade-a-trade contra o Zeus, então mostra TODAS as
 * linhas — inclusive as retificadas (superseded). O problema: somar a coluna
 * "Valor (R$)" na mão dupla-conta os retificados. O rodapé desfaz a ambiguidade
 * separando o que CONTA do que NÃO conta:
 *
 * - `vigenteNet`    = Σ valor_liquido das linhas vigentes (superseded_by == null).
 *   É o net entradas−saídas = o card 6DU (mesma base do Fluxo Δ=R$0 pós-#232).
 * - `retificadoNet` = Σ valor_liquido das linhas retificadas (superseded_by != null).
 *   Visíveis na tabela, FORA do total.
 * - `totalColuna`   = vigenteNet + retificadoNet = soma ingênua da coluna inteira.
 *
 * `valor_liquido` já vem signed do backend (+venda / −compra; ver
 * lancamentos_adapter._trades_per_row), então o net sai direto da soma.
 *
 * NB: opera sobre as linhas JÁ filtradas por BU. Com filtroBu="todas",
 * `vigenteNet` == card 6DU; sob filtro de BU o rodapé reconcilia a tabela
 * visível (subconjunto), não o card inteiro. Comercial_Indra/contraparte "NA"
 * NÃO são internos (varejo externo real) e contam normalmente; o único interno
 * (eh_interno=6286, Indra×Indra) já sai no canonical.
 */
interface Subtotais6du {
  vigenteNet: number;
  retificadoNet: number;
  totalColuna: number;
  nVigente: number;
  nRetificado: number;
}

function _subtotais6du(rows: LiquidacaoRow[]): Subtotais6du {
  let vigenteNet = 0;
  let retificadoNet = 0;
  let nVigente = 0;
  let nRetificado = 0;
  for (const r of rows) {
    const v = _toNum(r.valor_liquido);
    if (r.superseded_by != null) {
      retificadoNet += v;
      nRetificado += 1;
    } else {
      vigenteNet += v;
      nVigente += 1;
    }
  }
  return { vigenteNet, retificadoNet, totalColuna: vigenteNet + retificadoNet, nVigente, nRetificado };
}

// Task #42/#50: linha de export. `__tot` marca subtotais apensados no fim —
// formatters de colunas irrelevantes pro total saem em branco.
type LiqExport = LiquidacaoRow & Record<string, unknown> & { __tot?: boolean };

// Task #50 (WYSIWYG): monta colunas + linhas do export do 6DU espelhando a
// tabela na tela. Recebe `liqsExibidas` (já filtrada pelo toggle do #47),
// `subtotais` (sobre TODAS as linhas filtradas, p/ os totais), e o flag do
// toggle. Toggle OFF → só vigentes, sem coluna "Situação" (redundante), 1
// subtotal "Total vigente". Toggle ON → tudo + coluna "Situação"
// (Vigente/Retificado, #42) + os 2 subtotais. Pura → testável.
function _buildLiqExport(
  liqsExibidas: LiquidacaoRow[],
  subtotais: Subtotais6du,
  mostrarRetificados: boolean,
): { columns: ExportColumn<LiqExport>[]; rows: LiqExport[] } {
  const columnsBase: ExportColumn<LiqExport>[] = [
    { key: "id_trade_origem", header: "ID Trade", format: (r) => (r.__tot ? "" : r.id_trade_origem) },
    { key: "id_boleta", header: "#BOLETA", format: (r) => (r.__tot ? "" : (r.id_boleta ?? "")) },
    { key: "contraparte_nome", header: "Contraparte", format: (r) => r.contraparte_nome },
    { key: "tipo", header: "Tipo", format: (r) => (r.__tot ? "" : _tipoLabel(r.tipo)) },
    { key: "submercado", header: "Submerc", format: (r) => (r.__tot ? "" : (r.submercado ?? "")) },
    { key: "volume", header: "Vol (MWh)", format: (r) => (r.__tot ? "" : num(Math.abs(_toNum(r.volume)), 4)) },
    { key: "preco_unitario", header: "Preço (R$/MWh)", format: (r) => (r.__tot ? "" : num(_toNum(r.preco_unitario), 2)) },
    { key: "valor", header: "Valor (R$)", format: (r) => brlInt(_toNum(r.valor_liquido)) },
    { key: "num_contrato_bbce", header: "Contrato BBCE", format: (r) => (r.__tot ? "" : (r.num_contrato_bbce ?? "")) },
    { key: "data_pagamento", header: "Data", format: (r) => (r.__tot ? "" : dateCell(r.data_pagamento)) },
    { key: "business_unit", header: "BU", format: (r) => (r.__tot ? "" : buLabel(r.business_unit)) },
  ];
  const columns: ExportColumn<LiqExport>[] = mostrarRetificados
    ? [
        ...columnsBase,
        { key: "situacao", header: "Situação", format: (r) => (r.__tot ? "" : (r.superseded_by != null ? "Retificado" : "Vigente")) },
      ]
    : columnsBase;

  const base = liqsExibidas as unknown as LiqExport[];
  if (base.length === 0) return { columns, rows: base };
  const subRow = (label: string, net: number): LiqExport =>
    ({
      id_trade_origem: 0,
      data_pagamento: "",
      contraparte_nome: label,
      tipo: "venda",
      business_unit: "atacado",
      submercado: null,
      volume: null,
      preco_unitario: null,
      valor: String(net),
      valor_liquido: String(net),
      num_contrato_bbce: null,
      id_boleta: null,
      superseded_by: null,
      __tot: true,
    }) as unknown as LiqExport;
  const subtotalRows = mostrarRetificados
    ? [
        subRow("TOTAL VIGENTE (= card 6DU)", subtotais.vigenteNet),
        subRow("Retificados (não contam)", subtotais.retificadoNet),
      ]
    : [subRow("TOTAL VIGENTE (= card 6DU)", subtotais.vigenteNet)];
  return { columns, rows: [...base, ...subtotalRows] };
}

// Exportados pra teste; mantém superfície clara.
export const __test__ = { _aggregateNet, _totalNet, _subtotais6du, _buildLiqExport };

export function LiquidacoesNetTabs({ filtroBu, dataAlvo, cardLiquido6du }: Props) {
  const [tab, setTab] = useState<Tab>("liq6du");
  const [state, setState] = useState<State>({ rows: [], loading: true });
  // Task #47: a conferência da Mesa é contra o estado ATIVO do Zeus, que NÃO
  // lista retificados. Default = só vigentes (alinha Cortex ativo = Zeus ativo
  // = card de cima). Toggle reexibe os retificados (greyed) sem perder a trilha.
  const [mostrarRetificados, setMostrarRetificados] = useState(false);

  useEffect(() => {
    // Fix #158: espera o calendário CCEE resolver no pai. Antes o
    // componente computava `calcularProximo6DU(new Date())` direto
    // — heurística OFFLINE que ignora feriados móveis e mandava
    // `data_alvo` errado pro backend (Corpus Christi 04/06/26 fazia
    // o componente buscar trades em 08/06 quando o 6du oficial é 09/06).
    if (!dataAlvo) return;
    let cancel = false;
    setState({ rows: [], loading: true });
    const dataAlvoIso = ymdIso(dataAlvo);
    getLiquidacoes({ data_alvo: dataAlvoIso })
      .then((resp) => {
        if (cancel) return;
        setState({ rows: resp.liquidacoes_6du, loading: false });
      })
      .catch(() => {
        if (cancel) return;
        // Falha silenciosa: header global do #48b cobre o badge demo.
        setState({ rows: [], loading: false });
      });
    return () => {
      cancel = true;
    };
  }, [dataAlvo]);

  // Filtro de BU client-side (mantido do #49). Só linhas atacado/varejo
  // entram na UI — `compartilhada/gestao` não fazem sentido em fluxo Zeus.
  const liqsFiltradas = useMemo(() => {
    const buOk = (r: LiquidacaoRow) =>
      r.business_unit === "atacado" || r.business_unit === "varejo";
    const matchFiltro = (r: LiquidacaoRow) =>
      filtroBu === "todas" || r.business_unit === filtroBu;
    return state.rows.filter((r) => buOk(r) && matchFiltro(r));
  }, [state.rows, filtroBu]);

  const netRows = useMemo(() => _aggregateNet(liqsFiltradas), [liqsFiltradas]);
  const netTotal = useMemo(() => _totalNet(netRows), [netRows]);

  // Task #42: subtotais do rodapé da aba detalhada (vigente=card vs retificado).
  const subtotais = useMemo(() => _subtotais6du(liqsFiltradas), [liqsFiltradas]);

  // Task #45: reconciliação EXATA com o card 6DU via linha de dust. A tabela
  // filtra trades-poeira (< R$0,50); o card (/api/fluxo) não. dust = card −
  // Total vigente exibido = a soma dos trades-poeira ocultos. Só com all-BU (o
  // card é all-BU; sob filtro de BU o vigente é subconjunto e não reconcilia).
  const reconciliaDust =
    filtroBu === "todas" &&
    typeof cardLiquido6du === "number" &&
    Number.isFinite(cardLiquido6du);
  const dust = reconciliaDust ? (cardLiquido6du as number) - subtotais.vigenteNet : 0;
  const temDust = reconciliaDust && Math.abs(dust) >= 0.005;

  // Task #47: linhas EXIBIDAS na tabela. Default só vigentes; com o toggle
  // ligado, reaparecem os retificados (superseded). O Export sempre leva TUDO
  // (liqExportRows abaixo) — independe do toggle.
  const liqsExibidas = useMemo(
    () =>
      mostrarRetificados
        ? liqsFiltradas
        : liqsFiltradas.filter((r) => r.superseded_by == null),
    [liqsFiltradas, mostrarRetificados],
  );

  // ---- Export columns ----
  // Task #74: colunas numericas/datas viram FormattedCell pra Excel
  // interpretar como NUMERO/DATA nativo. Mesa pode SUM/AVG/agregar em
  // pivot table. Strings (Contraparte, Tipo, BU, Contrato BBCE) seguem
  // como texto.
  // Task #42: `__tot` marca as linhas de subtotal apensadas no fim do export —
  // os formatters de colunas irrelevantes pra um total (id, tipo, vol, preço,
  // data, BU) saem em branco; só Contraparte (label) e Valor (R$) preenchem.
  // Task #50 (WYSIWYG): colunas + linhas do export espelham a tabela na tela
  // (liqsExibidas + toggle). Lógica pura em `_buildLiqExport` (testável).
  const { columns: liqColumns, rows: liqExportRows } = useMemo(
    () => _buildLiqExport(liqsExibidas, subtotais, mostrarRetificados),
    [liqsExibidas, subtotais, mostrarRetificados],
  );

  type NetExport = NetRow & Record<string, unknown>;
  const netColumns: ExportColumn<NetExport>[] = [
    { key: "contraparte", header: "Contraparte" },
    { key: "compras_R", header: "Compras (R$)", format: (r) => brlInt(r.compras_R) },
    { key: "vendas_R", header: "Vendas (R$)", format: (r) => brlInt(r.vendas_R) },
    { key: "net_fin", header: "Net Financeiro", format: (r) => brlInt(r.net_fin) },
    { key: "vol_compras", header: "Vol Compras (MWh)", format: (r) => num(r.vol_compras, 4) },
    { key: "vol_vendas", header: "Vol Vendas (MWh)", format: (r) => num(r.vol_vendas, 4) },
    { key: "net_eng", header: "Net Energético", format: (r) => num(r.net_eng, 4) },
    { key: "bu", header: "BU", format: (r) => buLabel(r.bu) },
  ];

  const dataLabel = new Date().toISOString().slice(0, 10);
  const exportSheetsLabel = "6DU + Net (Excel)";

  // Fix #158: distingue dois empty states que antes colidiam no mesmo
  // texto "Sem liquidações para essa BU" — enganoso quando a causa real
  // era data errada (heurística local), não filtro de BU. Agora:
  // - canonical vazio na data alvo  -> "Aguardando lançamentos canonical para DD/MM/AAAA"
  // - canonical tem trades mas filtroBu removeu tudo -> "Sem liquidações para essa BU"
  const dataAlvoLabel = dataAlvo ? formatarDataCurtaBr(dataAlvo) : null;
  const canonicalVazio = state.rows.length === 0;
  // Task #47: caso novo — há linhas (retificadas), mas o default vigente-only
  // esvaziou a tabela. Mensagem aponta os ocultos em vez de sugerir BU/data.
  const soRetificadosOcultos =
    !canonicalVazio && liqsFiltradas.length > 0 && liqsExibidas.length === 0;
  const msgVazioLiq = soRetificadosOcultos
    ? `Sem trades vigentes nesse 6DU — ${subtotais.nRetificado} retificado(s) oculto(s).`
    : canonicalVazio && dataAlvoLabel
      ? `Aguardando lançamentos canonical para ${dataAlvoLabel}.`
      : "Sem liquidações para essa BU.";
  const msgVazioNet =
    canonicalVazio && dataAlvoLabel
      ? `Aguardando lançamentos canonical para ${dataAlvoLabel}.`
      : "Sem dados para essa BU.";

  return (
    <div className="bg-indra-azul-surface rounded-xl border border-white/10 p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <ToggleTabs
          tabs={[
            { id: "liq6du" as Tab, label: "Liquidações 6DU (detalhado por trade)" },
            { id: "net" as Tab, label: "Net por contraparte" },
          ]}
          active={tab}
          onChange={setTab}
        />
        <div className="flex items-center gap-3">
        {/* Task #47: toggle de retificados — só faz sentido quando há algum. */}
        {tab === "liq6du" && !state.loading && subtotais.nRetificado > 0 && (
          <button
            type="button"
            data-testid="liq6du-toggle-retificados"
            aria-pressed={mostrarRetificados}
            onClick={() => setMostrarRetificados((v) => !v)}
            className={
              "flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border transition-colors " +
              (mostrarRetificados
                ? "border-indra-turquesa/40 text-indra-turquesa bg-indra-turquesa/10"
                : "border-white/10 text-white/60 hover:text-white hover:bg-white/5")
            }
          >
            <span
              className={
                "inline-block w-2 h-2 rounded-full " +
                (mostrarRetificados ? "bg-indra-turquesa" : "bg-white/30")
              }
            />
            {mostrarRetificados ? "Ocultar retificados" : "Mostrar retificados"}
          </button>
        )}
        {tab === "liq6du" ? (
          <ExportDropdown<LiqExport>
            filename={`cortex-liquidacoes-${dataLabel}`}
            primarySheetName="Liquidações 6DU"
            extraSheetsLabel={exportSheetsLabel}
            columns={liqColumns}
            rows={liqExportRows}
            extraSheets={[
              {
                name: "Net por contraparte",
                columns: netColumns as unknown as ExportColumn<Record<string, unknown>>[],
                rows: netRows as unknown as Array<Record<string, unknown>>,
              },
            ]}
          />
        ) : (
          <ExportDropdown<NetExport>
            filename={`cortex-net-${dataLabel}`}
            primarySheetName="Net por contraparte"
            extraSheetsLabel={exportSheetsLabel}
            columns={netColumns}
            rows={netRows as unknown as NetExport[]}
            extraSheets={[
              {
                name: "Liquidações 6DU",
                columns: liqColumns as unknown as ExportColumn<Record<string, unknown>>[],
                rows: liqExportRows as unknown as Array<Record<string, unknown>>,
              },
            ]}
          />
        )}
        </div>
      </div>

      {/* ---- Aba 1: Liquidações detalhada ---- */}
      {tab === "liq6du" && (
        <div className="overflow-auto max-h-[640px]" data-testid="liq-table-wrapper">
          {state.loading ? (
            <div
              className="text-white/40 text-sm py-8 text-center"
              data-testid="liq6du-loading"
            >
              Carregando liquidações…
            </div>
          ) : (
            <table className="tbl">
              <thead className="sticky top-0 z-10 bg-indra-azul-surface">
                <tr>
                  <th className="num">ID Trade</th>
                  <th className="num">#BOLETA</th>
                  <th>Contraparte</th>
                  {/* Task #139: header de coluna com badge (COMPRA/VENDA)
                      vai ao centro pra casar com o conteudo da td. */}
                  <th className="center">Tipo</th>
                  <th>Submerc</th>
                  <th className="num">Vol (MWh)</th>
                  <th className="num">Preço (R$/MWh)</th>
                  <th className="num">Valor (R$)</th>
                  <th>Contrato BBCE</th>
                  <th>Data</th>
                  <th>BU</th>
                </tr>
              </thead>
              <tbody>
                {liqsExibidas.length === 0 ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="text-center text-white/40 py-8"
                      data-testid="liq-empty-msg"
                    >
                      {msgVazioLiq}
                    </td>
                  </tr>
                ) : (
                  liqsExibidas.map((l) => {
                    const valorLiq = _toNum(l.valor_liquido);
                    const isSuperseded = l.superseded_by != null;
                    return (
                      <tr
                        key={l.id_trade_origem}
                        style={{ height: 38 }}
                        className={isSuperseded ? "opacity-40" : ""}
                        title={isSuperseded ? `Trade retificado pelo ID ${l.superseded_by}. Não impacta agregados de caixa.` : undefined}
                      >
                        <td className="num text-white/60 font-mono text-xs">
                          {l.id_trade_origem}
                        </td>
                        <td
                          className="num text-white/70 font-mono text-xs"
                          data-testid={`boleta-${l.id_trade_origem}`}
                        >
                          {l.id_boleta ?? "-"}
                        </td>
                        <td
                          className="font-medium max-w-[260px] truncate"
                          title={l.contraparte_nome}
                        >
                          {l.contraparte_nome}
                        </td>
                        <td className="center">
                          <span
                            data-testid={`tipo-badge-${l.tipo}`}
                            className={
                              "inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded " +
                              (l.tipo === "compra"
                                ? "bg-[#FFEBEE] text-[#C62828]"
                                : "bg-[#E8F5E9] text-[#2E7D32]")
                            }
                          >
                            {_tipoLabel(l.tipo)}
                          </span>
                          {isSuperseded && (
                            <span
                              data-testid={`retificado-badge-${l.id_trade_origem}`}
                              className="inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ml-1 bg-white/10 text-white/40"
                            >
                              Retificado
                            </span>
                          )}
                        </td>
                        <td className="text-white/70">{l.submercado ?? "-"}</td>
                        <td className="num text-white/80">
                          {l.volume !== null ? _fmtVol(_toNum(l.volume)) : "-"}
                        </td>
                        <td className="num text-white/80">
                          {l.preco_unitario !== null ? _fmtPreco(_toNum(l.preco_unitario)) : "-"}
                          {/* Task #214: badge nos PLD+ de mês fechado cujo preço
                              foi trocado p/ PLD realizado + ágio (#213). */}
                          {l.ajustado && (
                            <span
                              data-testid={`liq-badge-ajustado-${l.id_trade_origem}`}
                              className="ml-1.5 inline-block align-middle text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-indra-turquesa/15 text-indra-turquesa cursor-help"
                              title={
                                "Preço corrigido pós-fechamento: PLD realizado + ágio. " +
                                `Zeus (forward): ${_fmtPreco(_toNum(l.preco_zeus ?? null))} → ` +
                                `efetivo: ${_fmtPreco(_toNum(l.preco_efetivo ?? null))}.`
                              }
                            >
                              ajustado
                            </span>
                          )}
                        </td>
                        <td
                          className={
                            "num " + (valorLiq >= 0 ? "text-indra-positivo" : "text-indra-negativo")
                          }
                        >
                          {fmtBRL(valorLiq, { decimals: 0 })}
                        </td>
                        <td className="text-white/60 font-mono text-xs">
                          {l.num_contrato_bbce ?? "-"}
                        </td>
                        <td className="text-white/70">
                          {formatarDataCurtaBr(_ymdToDate(l.data_pagamento))}
                        </td>
                        <td className="text-white/70">{buLabel(l.business_unit)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {/* Task #47: rodapé. Default vigente-only → "Total vigente" = soma
                  das linhas exibidas = card 6DU. Os retificados ficam fora; com
                  o toggle OFF aparecem como nota discreta de ocultos, com o
                  toggle ON viram subtotal "Retificados (não contam)". */}
              {liqsFiltradas.length > 0 && (
                <tfoot className="sticky bottom-0 z-10 bg-indra-azul-deep border-t border-white/20">
                  <tr style={{ height: 42 }} data-testid="liq6du-total-vigente-row">
                    <td
                      colSpan={7}
                      className="font-bold text-white uppercase text-xs tracking-wider"
                    >
                      Total vigente{" "}
                      <span className="text-white/40 normal-case font-normal">
                        {temDust
                          ? `(exibido · ${subtotais.nVigente} trades)`
                          : `(= card 6DU · ${subtotais.nVigente} trades)`}
                      </span>
                    </td>
                    <td
                      className={
                        "num font-bold " +
                        (subtotais.vigenteNet >= 0 ? "text-indra-turquesa" : "text-indra-negativo")
                      }
                      data-testid="liq6du-total-vigente"
                    >
                      {fmtBRL(subtotais.vigenteNet, { decimals: 0 })}
                    </td>
                    <td colSpan={3} />
                  </tr>
                  {/* Task #45: reconciliação EXATA com o card via linha de dust.
                      A tabela esconde trades-poeira (< R$0,50); dust = card −
                      vigente exibido. Só aparece quando há dust (≠ 0). */}
                  {temDust && (
                    <tr style={{ height: 28 }} data-testid="liq6du-dust-reconc-row">
                      <td colSpan={7} className="text-white/40 text-[10px] normal-case">
                        Exibido {fmtBRL(subtotais.vigenteNet, { decimals: 2 })}
                        {" · descartados < R$0,50 (dust) "}
                        {fmtBRL(dust, { decimals: 2 })}
                        {" · = Card 6DU "}
                        {fmtBRL(cardLiquido6du as number, { decimals: 2 })}
                      </td>
                      <td
                        className="num text-white/40 text-[10px]"
                        data-testid="liq6du-dust"
                      >
                        {fmtBRL(dust, { decimals: 2 })}
                      </td>
                      <td colSpan={3} />
                    </tr>
                  )}
                  {mostrarRetificados ? (
                    <tr style={{ height: 36 }} data-testid="liq6du-total-retificado-row">
                      <td colSpan={7} className="text-white/45 text-xs">
                        Retificados <span className="text-white/30">(não contam · {subtotais.nRetificado} trades)</span>
                      </td>
                      <td className="num text-white/45" data-testid="liq6du-total-retificado">
                        {fmtBRL(subtotais.retificadoNet, { decimals: 0 })}
                      </td>
                      <td colSpan={3} />
                    </tr>
                  ) : subtotais.nRetificado > 0 ? (
                    <tr style={{ height: 32 }} data-testid="liq6du-retificados-ocultos-row">
                      <td colSpan={11} className="text-white/40 text-xs">
                        {subtotais.nRetificado} retificado(s) oculto(s) ({fmtBRL(subtotais.retificadoNet, { decimals: 0 })}) —{" "}
                        <button
                          type="button"
                          data-testid="liq6du-mostrar-retificados-link"
                          onClick={() => setMostrarRetificados(true)}
                          className="underline hover:text-white"
                        >
                          mostrar
                        </button>
                      </td>
                    </tr>
                  ) : null}
                </tfoot>
              )}
            </table>
          )}
        </div>
      )}

      {/* ---- Aba 2: Net por contraparte ---- */}
      {tab === "net" && (
        <div className="overflow-auto max-h-[640px]" data-testid="net-table-wrapper">
          {state.loading ? (
            <div className="text-white/40 text-sm py-8 text-center">
              Carregando net por contraparte…
            </div>
          ) : (
            <table className="tbl">
              <thead className="sticky top-0 z-10 bg-indra-azul-surface">
                <tr>
                  <th>Contraparte</th>
                  <th className="num">Compras (R$)</th>
                  <th className="num">Vendas (R$)</th>
                  <th className="num">Net Financeiro</th>
                  <th className="num">Vol Compras (MWh)</th>
                  <th className="num">Vol Vendas (MWh)</th>
                  <th className="num">Net Energético</th>
                  <th>BU</th>
                </tr>
              </thead>
              <tbody>
                {netRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center text-white/40 py-8"
                      data-testid="net-empty-msg"
                    >
                      {msgVazioNet}
                    </td>
                  </tr>
                ) : (
                  netRows.map((r) => (
                    <tr key={`${r.contraparte}|${r.bu}`} style={{ height: 38 }}>
                      <td
                        className="font-medium max-w-[280px] truncate"
                        title={r.contraparte}
                      >
                        {r.contraparte}
                      </td>
                      <td className="num text-white/80">{fmtBRL(r.compras_R, { decimals: 0 })}</td>
                      <td className="num text-white/80">{fmtBRL(r.vendas_R, { decimals: 0 })}</td>
                      <td
                        className={
                          "num font-semibold " +
                          (r.net_fin >= 0 ? "text-indra-positivo" : "text-indra-negativo")
                        }
                        data-testid="net-fin-cell"
                      >
                        {fmtBRL(r.net_fin, { decimals: 0 })}
                      </td>
                      <td className="num text-white/80">{_fmtVol(r.vol_compras)}</td>
                      <td className="num text-white/80">{_fmtVol(r.vol_vendas)}</td>
                      <td
                        className={
                          "num " + (r.net_eng >= 0 ? "text-indra-positivo" : "text-indra-negativo")
                        }
                      >
                        {_fmtVol(r.net_eng)}
                      </td>
                      <td className="text-white/70">{buLabel(r.bu)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {netRows.length > 0 && (
                <tfoot className="sticky bottom-0 z-10 bg-indra-azul-deep border-t border-white/20">
                  <tr style={{ height: 42 }} data-testid="net-total-row">
                    <td className="font-bold text-white uppercase text-xs tracking-wider">
                      Total
                    </td>
                    <td className="num font-bold text-white">
                      {fmtBRL(netTotal.compras_R, { decimals: 0 })}
                    </td>
                    <td className="num font-bold text-white">
                      {fmtBRL(netTotal.vendas_R, { decimals: 0 })}
                    </td>
                    <td
                      className={
                        "num font-bold " +
                        (netTotal.net_fin >= 0 ? "text-indra-positivo" : "text-indra-negativo")
                      }
                      data-testid="net-total-fin"
                    >
                      {fmtBRL(netTotal.net_fin, { decimals: 0 })}
                    </td>
                    <td className="num font-bold text-white">
                      {_fmtVol(netTotal.vol_compras)}
                    </td>
                    <td className="num font-bold text-white">
                      {_fmtVol(netTotal.vol_vendas)}
                    </td>
                    <td
                      className={
                        "num font-bold " +
                        (netTotal.net_eng >= 0 ? "text-indra-positivo" : "text-indra-negativo")
                      }
                      data-testid="net-total-eng"
                    >
                      {_fmtVol(netTotal.net_eng)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      )}
    </div>
  );
}
